import React, { useState } from 'react';
import { FileText, Download, Calendar, Filter, BarChart3, Package, Users, FileCheck } from 'lucide-react';

const Reports = () => {
  const [reportType, setReportType] = useState('inventory');
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
    category: ''
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const reportTypes = [
    { value: 'inventory', label: 'Inventory Status Report', icon: Package, description: 'Current stock levels, expiry dates, low stock alerts' },
    { value: 'batch_production', label: 'Batch Production Report', icon: BarChart3, description: 'Production metrics, completed batches, timelines' },
    { value: 'component_usage', label: 'Component Usage Report', icon: FileText, description: 'Component consumption tracking across batches' },
    { value: 'traceability', label: 'Regulatory Traceability Report', icon: FileCheck, description: 'Full traceability for Health Canada compliance' }
  ];

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportType, filters })
      });
      
      const data = await response.json();
      setReportData(data);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!reportData) return;
    
    // Convert to CSV and download
    const csv = convertToCSV(reportData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const convertToCSV = (data) => {
    if (!data || !data.data || data.data.length === 0) return '';
    
    const headers = Object.keys(data.data[0]).filter(key => typeof data.data[0][key] !== 'object');
    const rows = data.data.map(item => 
      headers.map(header => JSON.stringify(item[header] || '')).join(',')
    );
    
    return [headers.join(','), ...rows].join('\n');
  };

  const selectedReportType = reportTypes.find(r => r.value === reportType);

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Reports & Analytics</h1>
        <p className="text-gray-600">Generate compliance reports and analytics</p>
      </div>

      {/* Report Type Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2 text-blue-600" />
          Select Report Type
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reportTypes.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.value}
                onClick={() => setReportType(type.value)}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  reportType === type.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start">
                  <Icon className={`h-6 w-6 mt-1 ${reportType === type.value ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div className="ml-3">
                    <div className={`font-semibold ${reportType === type.value ? 'text-blue-900' : 'text-gray-900'}`}>
                      {type.label}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">{type.description}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Filter className="h-5 w-5 mr-2 text-blue-600" />
          Filter Parameters
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Date From
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Date To
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Status Filter (for batch reports) */}
          {(reportType === 'batch_production' || reportType === 'traceability') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="QC Passed">QC Passed</option>
              </select>
            </div>
          )}

          {/* Category Filter (for inventory) */}
          {reportType === 'inventory' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                <option value="Active Pharmaceutical">Active Pharmaceutical</option>
                <option value="PPE Component">PPE Component</option>
                <option value="Documentation">Documentation</option>
                <option value="Packaging">Packaging</option>
                <option value="Base Kit">Base Kit</option>
              </select>
            </div>
          )}
        </div>

        {/* Generate Button */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={handleGenerateReport}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
          >
            <BarChart3 className="h-5 w-5" />
            <span>{loading ? 'Generating...' : 'Generate Report'}</span>
          </button>

          {reportData && (
            <button
              onClick={handleExport}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Download className="h-5 w-5" />
              <span>Export CSV</span>
            </button>
          )}
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900">{reportData.reportType}</h2>
            <p className="text-sm text-gray-600">Generated: {reportData.generatedAt}</p>
          </div>

          {/* Summary */}
          {reportData.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {Object.entries(reportData.summary).map(([key, value]) => (
                <div key={key} className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                  <div className="text-2xl font-bold text-blue-900 mt-1">{value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Data Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {reportData.data && reportData.data[0] && 
                    Object.keys(reportData.data[0])
                      .filter(key => typeof reportData.data[0][key] !== 'object' && key !== 'id')
                      .map(key => (
                        <th key={key} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </th>
                      ))
                  }
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reportData.data && reportData.data.slice(0, 10).map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    {Object.entries(row)
                      .filter(([key, value]) => typeof value !== 'object' && key !== 'id')
                      .map(([key, value]) => (
                        <td key={key} className="px-4 py-3 text-sm text-gray-900">
                          {value}
                        </td>
                      ))
                    }
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {reportData.data && reportData.data.length > 10 && (
            <p className="text-sm text-gray-600 mt-4 text-center">
              Showing 10 of {reportData.data.length} records. Export to CSV for full data.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
